// gatsby-browser.js

import React from "react"
import { getCookieConsentValue } from "react-cookie-consent"
import "@fontsource/poppins/400.css"
import "@fontsource/poppins/500.css"
import "@fontsource/poppins/600.css"
import "@fontsource/poppins/700.css"
import "./src/components/layout.css"
import "./src/components/HeaderTitle.css"
import "./src/components/FAQ.css"
import "./src/components/output.css"
import CookieBanner, { COOKIE_NAME } from "./src/components/CookieBanner"
import {
  buildStructuredData,
  getPageSeo,
  SITE_NAME,
} from "./src/seo/site-metadata"
import { captureMarketingAttribution } from "./src/utils/marketing-attribution"

const GA_MEASUREMENT_ID = "G-71PEXD2E5S" // GA4
const ADS_ID = "AW-17510022581" // Google Ads
function upsertMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`)

  if (!element) {
    element = document.createElement("meta")
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }

  element.setAttribute("content", content)
}

function syncSeoMetadata(pathname) {
  if (typeof document === "undefined") return

  const seo = getPageSeo(pathname)
  const structuredData = buildStructuredData(seo)

  document.title = seo.title
  document.documentElement.lang = "en-GB"

  upsertMeta("name", "description", seo.description)
  upsertMeta("name", "robots", seo.robots)
  upsertMeta("property", "og:site_name", SITE_NAME)
  upsertMeta("property", "og:locale", "en_GB")
  upsertMeta("property", "og:type", seo.type)
  upsertMeta("property", "og:title", seo.title)
  upsertMeta("property", "og:description", seo.description)
  upsertMeta("property", "og:url", seo.canonical)
  upsertMeta("property", "og:image", seo.image)
  upsertMeta("property", "og:image:alt", seo.title)
  upsertMeta("name", "twitter:card", "summary_large_image")
  upsertMeta("name", "twitter:creator", "@web_wonderland")
  upsertMeta("name", "twitter:title", seo.title)
  upsertMeta("name", "twitter:description", seo.description)
  upsertMeta("name", "twitter:image", seo.image)

  let canonical = document.head.querySelector('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement("link")
    canonical.setAttribute("rel", "canonical")
    document.head.appendChild(canonical)
  }
  canonical.setAttribute("href", seo.canonical)

  let jsonLd = document.getElementById("ww-structured-data")
  if (!jsonLd) {
    jsonLd = document.createElement("script")
    jsonLd.id = "ww-structured-data"
    jsonLd.type = "application/ld+json"
    document.head.appendChild(jsonLd)
  }
  jsonLd.textContent = JSON.stringify(structuredData)
}

/** Inject the gtag.js loader (once) and init dataLayer/gtag */
function ensureGtagLoaded() {
  if (typeof window === "undefined") return

  // Create dataLayer/gtag early if missing
  if (!window.dataLayer) window.dataLayer = []
  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments)
    }
  }

  // Avoid double-injecting the external script
  if (!document.getElementById("gtag-js")) {
    const script = document.createElement("script")
    script.id = "gtag-js"
    script.async = true
    // Loading with the GA4 ID is fine; Ads will be configured via gtag('config', ADS_ID)
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
    document.head.appendChild(script)
  }

  // Mark boot time (safe to call repeatedly)
  window.gtag("js", new Date())
}

/** Configure both GA4 and Ads IDs (safe to call multiple times) */
function configureAnalytics() {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return

  // GA4 config
  window.gtag("config", GA_MEASUREMENT_ID, {
    anonymize_ip: true,
  })

  // Google Ads config (required for conversion events to work)
  window.gtag("config", ADS_ID)
}

/** Public loader that runs once per session unless reloaded */
function loadGoogleTags() {
  if (typeof window === "undefined") return
  if (window._gtagLoaded) return

  ensureGtagLoaded()
  configureAnalytics()

  window._gtagLoaded = true
  // Back-compat in case older code checks this flag:
  window._gaLoaded = true
}

/** Track high-intent phone and email clicks without collecting link text or PII. */
function bindContactActionTracking() {
  if (typeof document === "undefined" || window._contactTrackingBound) return

  document.addEventListener("click", event => {
    const link = event.target.closest?.('a[href^="tel:"], a[href^="mailto:"]')

    if (!link || typeof window.gtag !== "function") return

    window.gtag("event", "contact_click", {
      contact_method: link.href.startsWith("tel:") ? "phone" : "email",
      link_location: window.location.pathname,
    })
  })

  window._contactTrackingBound = true
}

export const onInitialClientRender = () => {
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual"
  }

  // Load tags immediately on return visits if consent already granted
  if (getCookieConsentValue(COOKIE_NAME) === "true") {
    loadGoogleTags()
  }

  bindContactActionTracking()
  captureMarketingAttribution()
}

export const onRouteUpdate = ({ location }) => {
  captureMarketingAttribution()
  syncSeoMetadata(location.pathname)
}

export const shouldUpdateScroll = ({ routerProps: { location } }) => {
  if (location.hash) {
    return decodeURI(location.hash.slice(1))
  }

  // New page visits should start at the top. Gatsby can otherwise restore a
  // saved position for a route the visitor has already viewed, which makes
  // calls to action such as "Explore all case studies" land mid-page.
  // Keep Gatsby's saved-position behaviour for browser back/forward actions.
  if (location.action === "POP") {
    return true
  }

  return [0, 0]
}

export const wrapPageElement = ({ element }) => (
  <>
    {element}
    <CookieBanner
      onAccept={() => {
        loadGoogleTags()
      }}
      onDecline={() => {}}
    />
  </>
)
